import { Tabs } from 'antd';
import { useFormikContext } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DeriveMethod,
    FinanceProductBasedOn,
    type FinanceProductSettings,
    FinanceProductType,
    type FinderVehicleReferenceParametersPayload,
    type VariantReferenceParametersPayload,
} from '../../../../api/types';
import FinanceProductInitializer from '../FinanceProductInitializer';
import { includesFinancing } from '../shared/groupings';
import { InterestRateType, CommonType } from '../shared/typings';
import AssignVehiclesForm from './AssignVehiclesForm';
import ParametersForm from './ParametersForm';
import ProductDetailsForm from './ProductDetailsForm';
import BalloonTable from './tables/BalloonTable';
import DepositTable from './tables/DepositTable';
import DownPaymentTable from './tables/DownPaymentTable';
import FuelTaxTable from './tables/FuelTaxTable';
import InsuranceFeeTable from './tables/InsuranceFeeTable';
import InterestRateTable from './tables/InterestRateTable/index';
import LeasePurchaseTable from './tables/LeasePurchaseTable';
import LeaseReference from './tables/LeaseReferenceTable';
import LicenseTaxTable from './tables/LicenseTaxTable';

export enum Tab {
    MainDetails = 'MainDetails',
    Parameters = 'Parameters',
    DownPayment = 'DownPayment',
    Balloon = 'Balloon',
    Deposit = 'Deposit',
    Lease = 'Lease',
    InterestRate = 'InterestRate',
    ResidualValue = 'ResidualValue',
    Vehicles = 'Vehicles',
    LicenseTax = 'LicenseTax',
    FuelTax = 'FuelTax',
    InsuranceFee = 'InsuranceFee',
    LeasePurchase = 'LeasePurchase',
}

export const useTabs = () => {
    const { t } = useTranslation(['financeProductDetails']);

    const { values } = useFormikContext<FinanceProductFormValues>();

    const visible = useMemo(() => {
        const { type, deposit, interestRate, downPayment, balloon } = values;
        const depositTab = type === FinanceProductType.Lease && deposit?.type === CommonType.Table;

        const leaseTab = type === FinanceProductType.Lease;

        const ucclLeasingTab = type === FinanceProductType.UcclLeasing;

        const balloonTab =
            type === FinanceProductType.HirePurchaseWithBalloonGfv ||
            (type === FinanceProductType.HirePurchaseWithBalloon && balloon?.type === CommonType.Table);

        const leasePurchaseTab = type === FinanceProductType.LeasePurchase;

        const isFinancing = includesFinancing(type);

        const downPaymentTab = isFinancing && downPayment?.type === 'table';

        const interestRateTab = isFinancing && interestRate?.type === InterestRateType.Table;

        return {
            depositTab,
            leaseTab,
            ucclLeasingTab,
            downPaymentTab,
            interestRateTab,
            balloonTab,
            leasePurchaseTab,
        };
    }, [values]);

    return useMemo(
        () =>
            [
                <Tabs.TabPane
                    key={Tab.MainDetails}
                    tab={t('financeProductDetails:mainTitles.mainDetails')}
                    forceRender
                />,
                <Tabs.TabPane
                    key={Tab.Parameters}
                    tab={t('financeProductDetails:mainTitles.parameters')}
                    forceRender
                />,
                <Tabs.TabPane
                    key={Tab.Vehicles}
                    tab={t('financeProductDetails:mainTitles.assignVehicles')}
                    forceRender
                />,
                visible.depositTab && (
                    <Tabs.TabPane key={Tab.Deposit} tab={t('financeProductDetails:tables.deposit.title')} forceRender />
                ),
                visible.leaseTab && (
                    <Tabs.TabPane key={Tab.Lease} tab={t('financeProductDetails:tables.lease.title')} forceRender />
                ),
                visible.ucclLeasingTab && (
                    <Tabs.TabPane
                        key={Tab.LicenseTax}
                        tab={t('financeProductDetails:tables.ucclLeasing.licenseTax')}
                        forceRender
                    />
                ),
                visible.ucclLeasingTab && (
                    <Tabs.TabPane
                        key={Tab.FuelTax}
                        tab={t('financeProductDetails:tables.ucclLeasing.fuelTax')}
                        forceRender
                    />
                ),
                visible.ucclLeasingTab && (
                    <Tabs.TabPane
                        key={Tab.InsuranceFee}
                        tab={t('financeProductDetails:tables.ucclLeasing.insuranceFee')}
                        forceRender
                    />
                ),
                visible.balloonTab && (
                    <Tabs.TabPane key={Tab.Balloon} tab={t('financeProductDetails:tables.balloon.title')} forceRender />
                ),
                visible.leasePurchaseTab && (
                    <Tabs.TabPane
                        key={Tab.LeasePurchase}
                        tab={t('financeProductDetails:tables.leasePurchase.title')}
                        forceRender
                    />
                ),
                visible.downPaymentTab && (
                    <Tabs.TabPane
                        key={Tab.DownPayment}
                        tab={t('financeProductDetails:tables.downpayment.title')}
                        forceRender
                    />
                ),
                visible.interestRateTab && (
                    <Tabs.TabPane
                        key={Tab.InterestRate}
                        tab={t('financeProductDetails:tables.interestRate.title')}
                        forceRender
                    />
                ),
            ].filter(Boolean),
        [t, visible]
    );
};

const FinanceProductFormInner = ({
    activeTab,
    updated,
    offset,
    disabled = false,
}: {
    activeTab: string;
    updated?: string;
    offset?: string;
    disabled?: boolean;
}) => {
    switch (activeTab) {
        case Tab.MainDetails:
            return <ProductDetailsForm disabled={disabled} offset={offset} updated={updated} />;

        case Tab.Parameters:
            return <ParametersForm disabled={disabled} />;

        case Tab.Vehicles:
            return <AssignVehiclesForm disabled={disabled} />;

        case Tab.Deposit:
            return <DepositTable disabled={disabled} />;

        case Tab.Lease:
            return <LeaseReference disabled={disabled} />;

        case Tab.LicenseTax:
            return <LicenseTaxTable disabled={disabled} />;

        case Tab.FuelTax:
            return <FuelTaxTable disabled={disabled} />;

        case Tab.DownPayment:
            return <DownPaymentTable disabled={disabled} />;

        case Tab.InterestRate:
            return <InterestRateTable disabled={disabled} />;

        case Tab.InsuranceFee:
            return <InsuranceFeeTable disabled={disabled} />;

        case Tab.Balloon:
            return <BalloonTable disabled={disabled} />;

        case Tab.LeasePurchase:
            return <LeasePurchaseTable disabled={disabled} />;

        default:
            return null;
    }
};

const FinanceProductForm = ({
    activeTab,
    updated,
    offset,
    disabled = false,
}: {
    activeTab: string;
    offset?: string;
    updated?: string;
    disabled?: boolean;
}) => (
    <FinanceProductInitializer updated={updated}>
        <FinanceProductFormInner activeTab={activeTab} disabled={disabled} offset={offset} updated={updated} />
    </FinanceProductInitializer>
);

export default FinanceProductForm;

export type FinderVehicleReferenceParameterPayload = Omit<FinderVehicleReferenceParametersPayload, 'variants'> & {
    // in form values, variants is a string comma separated value
    variants?: string;
};

export type FinanceProductFormValues = Omit<FinanceProductSettings, 'vehicleReferenceParameters'> & {
    vehicleReferenceParameters: {
        finderVehicle: FinderVehicleReferenceParameterPayload;
        variant: VariantReferenceParametersPayload;
    };
};

export const defaultValues: FinanceProductFormValues = {
    displayName: '',
    legalName: {
        defaultValue: '',
        overrides: [],
    },
    description: {
        defaultValue: '',
        overrides: [],
    },
    identifier: '',
    bankReferenceIdentifier: null,
    bankId: '',
    period: null,
    order: 0,
    isActive: true,
    type: null,
    deriveMethod: DeriveMethod.Formula,
    basedOn: FinanceProductBasedOn.DownPayment,
    variantSuiteIds: [],
    vehicleReferenceParameters: {
        finderVehicle: { engineType: [], conditions: [], isAddOnSpecificVehicles: false },
        variant: { engineType: [], isAddOnSpecificVehicles: false },
    },
    term: null,
    webCalcSettingId: null,
};
